import Quill, { type QuillOptions } from 'quill'
import 'quill/dist/quill.snow.css'
import React, { useEffect, useRef, useState } from 'react'
import { pdfjs } from 'react-pdf'

import FilePreviewCard from '../../../pages/IncidentPage/FileTypes'
import { useStoreContext } from '../../../store/StoreContext'
import {
  convertStorage,
  parseFileInfoFromUrl
} from '../../../utils/IncidentUtil'

import MediaPreviewGrid from './MediaPreviewGrid'
import MediaPreviewModal from './MediaPreviewModal'
import { ReadOnlyQuillProps } from './quill.types'

// Setup PDF.js worker for text/code preview support
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

const ReadOnlyQuill: React.FC<ReadOnlyQuillProps> = ({
  content,
  commentId,
  noStyle = false,
  incidentActivity,
  download
}) => {
  const quillRef = useRef<Quill | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const [previewFile, setPreviewFile] = useState<any>(null)
  const [isPreviewOpen, setPreviewOpen] = useState(false)
  const [isFileLoading, setFileLoading] = useState(true)
  const [fileContent, setFileContent] = useState<string>('')

  const store = useStoreContext()

  useEffect(() => {
    if (previewFile) {
      setFileLoading(true)
    }
  }, [previewFile])

  const handleClosePreview = () => {
    setPreviewOpen(false)
    store.uiStore.setSidebarExpanded(true)
    setPreviewFile(null)
  }

  useEffect(() => {
    if (!containerRef.current) return

    const container = containerRef.current
    const editorContainer = container.appendChild(
      container.ownerDocument.createElement('div')
    )

    const options: QuillOptions = {
      theme: 'snow',
      readOnly: true,
      modules: {
        toolbar: false
      }
    }

    const quill = new Quill(editorContainer, options)
    quillRef.current = quill

    if (content) {
      quill.setContents(content)
    }

    return () => {
      if (container) {
        container.innerHTML = ''
      }
      if (quillRef.current) {
        quillRef.current = null
      }
    }
  }, [content, commentId])

  useEffect(() => {
    const lowerFile = previewFile?.toLowerCase() || ''
    const isCodeOrText =
      /\.(txt|log|md|csv|xml|json|ts|tsx|js|jsx|py|java|html|css|sh|yml|yaml)$/i.test(
        lowerFile
      )

    if (previewFile && isCodeOrText) {
      setFileLoading(true)
      fetch(previewFile)
        .then((res) => res.text())
        .then((text) => {
          setFileContent(text)
          setFileLoading(false)
        })
        .catch((err) => {
          console.error('Error loading file:', err)
          setFileLoading(false)
        })
    } else {
      setFileContent('')
      setFileLoading(false)
    }
  }, [previewFile])

  // File preview handler
  const handlePreview = (url: string) => {
    setPreviewOpen(true)
    store.uiStore.setSidebarExpanded(false)
    setPreviewFile(url)
  }

  return (
    <>
      <div className='flex flex-col'>
        {/* {!download && ( */}
        <div>
          <MediaPreviewGrid
            attachedFiles={incidentActivity?.attachedFiles}
            onPreview={handlePreview}
            download={download}
          />

          <div className='flex flex-wrap gap-[12px]'>
            {incidentActivity?.attachedFiles?.map((media: any) => {
              const isHeic =
                media.includes('heic') ||
                media.includes('heif') ||
                media.endsWith('.heic') ||
                media.endsWith('.heif')
              const isImage = media.includes('image/') && !isHeic
              const isVideo = media.includes('video')
              const fileInfo = parseFileInfoFromUrl(media)

              return (
                <React.Fragment key={media}>
                  {!isImage && !isVideo && (
                    <FilePreviewCard
                      file={{
                        name: fileInfo.name,
                        type: fileInfo.type,
                        url: media,
                        size: convertStorage(fileInfo.size)
                      }}
                      uploadType='display'
                      download={download}
                    />
                  )}
                </React.Fragment>
              )
            })}
          </div>
          <MediaPreviewModal
            isPreviewOpen={isPreviewOpen}
            handleClosePreview={handleClosePreview}
            previewFile={previewFile}
          />
        </div>
        {/* )} */}
        <div ref={containerRef} />
      </div>
    </>
  )
}

export default ReadOnlyQuill
